import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/da2j75jd3r93mn1k39s0";

const categoryOptions = ["动作热血", "恋爱校园", "奇幻异世界", "悬疑惊悚", "搞笑日常", "都市职场", "其他"];

export default function SubmitForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // anti-spam honeypot check
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? responseText;
      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "网络异常，请稍后再试。");
    }
  };

  return (
    <section id="submit" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-background-200 bg-background-100 lg:grid-cols-5">
          <div className="relative flex flex-col justify-center p-7 md:p-10 lg:col-span-2">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Colorful%20abstract%20Korean%20webtoon%20manhwa%20background%20with%20floating%20speech%20bubbles%20and%20comic%20stars%2C%20warm%20coral%20amber%20and%20teal%20palette%2C%20playful%20vibrant%20youthful%20style&width=800&height=900&seq=submit-bg&orientation=portrait"
                alt="提交收录韩漫站点装饰背景"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-tr from-primary-700/90 via-primary-600/80 to-accent-600/70"></div>
            <div className="relative z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50 ring-1 ring-background-50/30">
                <i className="ri-send-plane-line"></i>
                提交收录
              </span>
              <h2 className="mt-3 font-heading text-2xl text-background-50 md:text-3xl">推荐一个韩漫站点</h2>
              <p className="mt-3 text-sm leading-relaxed text-background-100/90">
                发现了好用的韩漫网址入口？填写右侧表单告诉我们，我们整理后会把它加入对应的分类导航，帮助更多喜欢韩漫的朋友。
              </p>
            </div>
          </div>

          <div className="p-7 md:p-10 lg:col-span-3">
            <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">站点名称</label>
                <input
                  type="text"
                  name="site_name"
                  required
                  placeholder="例如：韩漫屋"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">站点地址</label>
                <input
                  type="text"
                  name="site_url"
                  required
                  placeholder="例如：https://example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">题材分类</label>
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.map((opt) => (
                    <label key={opt} className="inline-flex cursor-pointer items-center gap-1.5">
                      <input type="radio" name="category" value={opt} defaultChecked={opt === "其他"} className="accent-primary-500" />
                      <span className="text-sm text-foreground-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">一句话简介（选填）</label>
                <textarea
                  name="intro"
                  maxLength={500}
                  rows={3}
                  placeholder="简单介绍这个站点的特点，最多 500 字"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                ></textarea>
              </div>

              {/* anti-spam honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  收到你的推荐啦！我们会尽快审核并整理收录，感谢分享。
                </p>
              )}
              {status === "error" && (
                <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === "loading" ? "提交中…" : "提交收录"}
                <i className="ri-send-plane-line"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}